import {Sync} from '@client/sync'
import {LinkButtonSmall} from '@components/common/button/button'
import {DropdownList} from '@components/common/dropdown-list/dropdown-list'
import {getTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import * as React from 'react'
import {Lang} from 'yougile-lang'
import {fixOldReportColumn, ReportColumn} from 'yougile-model'
import {ArrUtil, ObjUtil, Util} from 'yougile-util'
import {ButtonSelect} from '../../common/controls/button-select'
import {Option} from '../../common/select/select'
import {allFilters} from './filter-components'

interface Props {
  currentColumn: ReportColumn
  changeColumn: (newCol: ReportColumn) => void
  cloneColumn: () => void
  deleteColumn?: () => void
  canEdit: boolean
  isAggregatedColumn: boolean
  index: number
}

// eslint-disable-next-line max-lines-per-function
const FiltersBase = (props: Props) => {
  const {currentColumn: initialReport, changeColumn} = props
  const currentColumn = fixOldReportColumn(initialReport)
  const box = Sync.getCurrentSandbox()
  const taskCount = getTasks(
    box,
    currentColumn,
    undefined,
    undefined,
    props.index
  ).length

  const updateFilter =
    <T extends keyof ReportColumn['filter']>(filterName: T) =>
    (filter: ReportColumn['filter'][T]) => {
      if (!filter) {
        const ix = filters.findIndex(f => f === filterName)
        changeColumn({
          ...currentColumn,
          filter: ObjUtil.deletedAtKey(currentColumn.filter, filterName),
          activeFilters: ArrUtil.deletedAtIndex(filters, ix)
        })
      } else {
        changeColumn({
          ...currentColumn,
          filter: {...currentColumn.filter, [filterName]: filter}
        })
      }
    }

  const filters = currentColumn.activeFilters || []
  ObjUtil.forEach(currentColumn.filter, (k, v) => {
    if (!!v && !filters.includes(k)) {
      if (v instanceof Array) {
        if (v.length > 0) {
          filters.push(k)
        }
      } else if (!ObjUtil.isEmpty(v)) {
        filters.push(k)
      }
    }
  })

  const getOptions = (key?: keyof ReportColumn['filter']): Option[] => {
    return allFilters
      .filter(f => f.key === key || !filters.includes(f.key))
      .map(f => ({
        value: f.key,
        label: f.getName(),
        groupTitle: f.getGroupTitle()
      }))
  }

  const add = (value: keyof ReportColumn['filter']) => {
    changeColumn({
      ...currentColumn,
      activeFilters: [...filters, value]
    })
  }

  const changeFilter =
    (ix: number) =>
    (key: keyof ReportColumn['filter']): void => {
      const old = filters[ix]
      changeColumn({
        ...currentColumn,
        activeFilters: ArrUtil.changedAtIndex(filters, key, ix),
        filter: ObjUtil.deletedAtKey(currentColumn.filter, old)
      })
    }

  return (
    <div>
      <div className="pl-12 border-0 border-l-2 border-solid border-main-old flex items-center justify-between">
        <div className="text-third-old text-12 py-8">
          {Lang.msg.component.filters.filterGroup} №{props.index + 1}. (
          {taskCount} {Lang.msg.component.filters.filterGroupTasks})
        </div>
        {props.canEdit && !props.isAggregatedColumn && (
          <div className="flex">
            <LinkButtonSmall onClick={props.cloneColumn}>
              {Lang.msg.component.filters.clone}
            </LinkButtonSmall>
            {props.deleteColumn && (
              <LinkButtonSmall
                className="ml-16 text-error-old"
                onClick={props.deleteColumn}
              >
                {Lang.msg.component.filters.delete}
              </LinkButtonSmall>
            )}
          </div>
        )}
      </div>
      {filters.map((key, ix) => {
        const filter = allFilters.find(f => f.key === key)
        return !filter ? (
          <div key={ix}>Filter not found! ({key})</div>
        ) : (
          <div
            className="flex border-0 border-l-2 border-solid border-main-old odd:bg-main-even-old pl-12"
            key={ix}
          >
            <div className="text-third-old w-20 h-32 flex items-center text-12">
              {ix + 1}.
            </div>
            <div className="space-y-8 my-4">
              {Util.range(filter.getRows?.(currentColumn.filter) ?? 1).map(
                n => (
                  <div key={n}>
                    <DropdownList
                      className="w-184"
                      options={getOptions(key)}
                      value={key}
                      onChange={changeFilter(ix)}
                      disabled={n > 0}
                    />
                    <span className="w-16 inline-flex justify-center">:</span>
                    {filter.linesPerRow > 1 &&
                      Util.range(filter.linesPerRow - 1).map(i => (
                        <div key={i} className="h-32" />
                      ))}
                  </div>
                )
              )}
            </div>
            <div className="flex-1 my-4">
              {filter.getEl(currentColumn.filter, updateFilter)}
            </div>
          </div>
        )
      })}
      {getOptions().length > 0 && (
        <ButtonSelect
          className="mt-4 ml-32 pl-2"
          buttonLabel={Lang.msg.statsAddFilter}
          options={getOptions()}
          onSelect={add}
          placeholder={Lang.msg.statsChooseFilter}
        />
      )}
    </div>
  )
}

export const Filters = (props: Props) => {
  return <FiltersBase {...props} />
}
